/**
 * What a language is called, and which ones can be asked for.
 *
 * A language is offered once it has a folder in `src/locales` (SHIPPED_LANGUAGES).
 * Everything else here is only for the "ask for a language" list, which
 * emails support@ (POST /users/me/translation-request).
 */

/**
 * Names that Intl gets right but not helpfully. Plain `es` is the base every
 * Spanish speaker falls back to, written in neutral Latin American Spanish
 * (ustedes, no vosotros), so it is named for who it suits; `es-ES` holds only
 * the words Spain says differently.
 */
const NAME_OVERRIDES: Record<string, string> = {
  es: 'Español (Latinoamérica)',
  'es-ES': 'Español (España)',
  pt: 'Português',
  'pt-BR': 'Português (Brasil)',
  zh: '中文（简体）',
  'zh-TW': '中文（繁體）',
};

/** The language's own name for itself, e.g. `es` → "Español (Latinoamérica)". */
export function nativeName(code: string): string {
  if (NAME_OVERRIDES[code]) return NAME_OVERRIDES[code];
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    if (name) return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  } catch {
    // An engine without DisplayNames: the code is still something.
  }
  return code;
}

/** The language's name in English, for the support email and for English readers. */
export function englishName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Offered in the request list. Picked for the people who work on Australian
 * farms and nurseries, then the major world languages; "Another language"
 * covers the rest.
 */
export const REQUESTABLE_LANGUAGES: string[] = [
  'es', 'es-ES', 'pt-BR', 'fr', 'it', 'de', 'nl',
  'zh', 'zh-TW', 'vi', 'th', 'km', 'lo', 'my', 'id', 'ms', 'tl',
  'hi', 'pa', 'ne', 'bn', 'ur', 'ta', 'si',
  'ar', 'fa', 'tr', 'el', 'ko', 'ja',
  'to', 'sm', 'fj', 'bi', 'tpi',
  'ru', 'uk', 'pl', 'hr', 'sr', 'mk',
  'sw', 'am', 'so', 'ti',
];
